import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { canAccessEasyRetailReports, canAccessPosMonitor } from "../auth/roles";

const VENDORS = [
  { key: "IRINOX", label: "Irinox", icon: "bi-thermometer-sun" },
  { key: "RATIONAL", label: "Rational", icon: "bi-cpu" },
  { key: "FRIGOMAT", label: "Frigomat", icon: "bi-snow" },
  { key: "IFI_GALILEO", label: "IFI Galileo", icon: "bi-gear" },
  { key: "ELETTROBAR", label: "Elettrobar", icon: "bi-cup-straw" },
];

function NavItem({
  to,
  icon,
  label,
}: {
  to: string;
  icon: string;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `sidebar-link d-flex align-items-center gap-2 px-3 py-2 rounded-2 text-decoration-none transition ${isActive ? "sidebar-link-active" : "sidebar-link-inactive"}`
      }
    >
      <i className={`bi ${icon} sidebar-icon`} />
      <span>{label}</span>
    </NavLink>
  );
}

export default function MainLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isProfile = location.pathname === "/profile";
  const showPosMonitor = canAccessPosMonitor(user?.role);
  const showEasyRetailReports = canAccessEasyRetailReports(user?.role);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="app-shell d-flex" style={{ minHeight: "100vh" }}>
      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="sidebar-brand px-4 py-4">
          <div className="d-flex align-items-center gap-2">
            <div className="sidebar-logo">
              <i className="bi bi-grid-3x3-gap-fill" />
            </div>
            <div>
              <div className="fw-bold sidebar-brand-text">NAT Gestionale</div>
              <div className="sidebar-brand-sub text-muted">Control Room</div>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav px-2 pb-4">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Principale</div>
            <NavItem to="/dashboard" icon="bi-speedometer2" label="Dashboard" />
            <NavItem to="/machines" icon="bi-hdd-stack" label="Macchine" />
            {showPosMonitor ? <NavItem to="/pos-monitor" icon="bi-broadcast-pin" label="Monitor POS" /> : null}
            {showPosMonitor ? <NavItem to="/pos/products" icon="bi-box-seam" label="Prodotti cassa" /> : null}
            {showPosMonitor ? <NavItem to="/pos/epson-rt" icon="bi-printer" label="RT Epson" /> : null}
            {showPosMonitor ? <NavItem to="/pos/catalog" icon="bi-basket" label="Catalogo POS" /> : null}
            <NavItem to="/maintenance" icon="bi-wrench-adjustable-circle" label="Manutenzione" />
            <NavItem to="/personnel" icon="bi-people" label="Personale" />
            <NavLink to="/cash" className={({ isActive }) =>
              `sidebar-link d-flex align-items-center gap-2 px-3 py-2 rounded-2 text-decoration-none transition ${isActive ? "sidebar-link-active" : "sidebar-link-inactive"}`
            }>
              <i className="bi bi-cash-stack sidebar-icon" />
              <span>Cassa</span>
            </NavLink>
          </div>

          <div className="sidebar-section mt-4">
            <div className="sidebar-section-title">Provider</div>
            {VENDORS.map((v) => (
              <NavLink
                key={v.key}
                to={`/machines/${v.key}`}
                className={({ isActive }) =>
                  `sidebar-link sidebar-link-vendor d-flex align-items-center gap-2 px-3 py-2 rounded-2 text-decoration-none transition ${isActive ? "sidebar-link-active" : "sidebar-link-inactive"}`
                }
              >
                <i className={`bi ${v.icon} sidebar-icon`} />
                <span>{v.label}</span>
              </NavLink>
            ))}
          </div>

          <div className="sidebar-section mt-4">
            <div className="sidebar-section-title">Report</div>
            <NavItem to="/reports" icon="bi-journal-text" label="Report" />
            {showEasyRetailReports ? (
              <>
                <NavItem to="/reports/easy-retail" icon="bi-shop" label="Analisi Easy Retail" />
                <NavItem to="/reports/vne" icon="bi-cash-coin" label="Collegamento VNE" />
                <NavItem to="/reports/scales" icon="bi-speedometer2" label="Bilance" />
                <NavItem to="/reports/downloads" icon="bi-download" label="Scarica documenti" />
              </>
            ) : null}
          </div>

          <div className="sidebar-section mt-4">
            <div className="sidebar-section-title">Fatture</div>
            <NavItem to="/invoices" icon="bi-file-earmark-text" label="Fatture NAT" />
            <NavItem to="/received-invoices" icon="bi-file-earmark-arrow-down" label="Fatture ricevute" />
            <NavItem to="/invoicing/invoices" icon="bi-receipt" label="Fatture" />
            <NavItem to="/invoicing/received" icon="bi-inbox" label="Fatture ricevute" />
            <NavItem to="/invoicing/notifications" icon="bi-bell" label="Esiti / notifiche SDI" />
            <NavItem to="/invoicing/aruba-config" icon="bi-gear" label="Configurazione Aruba" />
            <NavItem to="/settings/electronic-invoicing/aruba" icon="bi-sliders" label="Fatturazione Aruba" />
            <NavItem to="/invoicing/sync-logs" icon="bi-list-check" label="Log sincronizzazione" />
          </div>

          <div className="sidebar-section mt-4 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <NavLink
              to="/profile"
              className={({ isActive }) =>
                `sidebar-link d-flex align-items-center gap-2 px-3 py-2 rounded-2 text-decoration-none transition mb-1 ${isActive ? "sidebar-link-active" : "sidebar-link-inactive"}`
              }
            >
              <i className="bi bi-person-circle sidebar-icon" />
              <span>{user?.fullName || user?.email || "Profilo"}</span>
            </NavLink>
            <button
              type="button"
              onClick={handleLogout}
              className="sidebar-link sidebar-link-inactive d-flex align-items-center gap-2 px-3 py-2 rounded-2 w-100 border-0 bg-transparent text-start"
            >
              <i className="bi bi-box-arrow-left sidebar-icon" />
              <span>Logout</span>
            </button>
          </div>
        </nav>
      </aside>

      {/* CONTENT */}
      <main className={`main-content ${isProfile ? "main-content-no-scroll" : ""}`}>
        <div className="content-inner">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
